import React, { useEffect, useState } from 'react';
import { sponsorsApi, formatApiError, resolveImageUrl } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { Plus, Save, Trash2, Upload, ExternalLink } from 'lucide-react';

const TIERS = [
  { value: 'presenting', label: 'Presenting' },
  { value: 'platinum', label: 'Platinum' },
  { value: 'gold', label: 'Gold' },
  { value: 'silver', label: 'Silver' },
  { value: 'bronze', label: 'Bronze' },
  { value: 'friend', label: 'Friend' },
];

const EMPTY = {
  name: '', tier: 'friend', tagline: '', description: '',
  logo_url: '', website_url: '', location: '',
  is_active: true, sort_order: 0,
};

export default function AdminSponsorsPage() {
  const [sponsors, setSponsors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null); // null = new, id = editing
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await sponsorsApi.adminList();
      setSponsors(res.data || []);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const pickForEdit = (s) => {
    setSelected(s.id);
    setForm({
      name: s.name || '',
      tier: s.tier || 'friend',
      tagline: s.tagline || '',
      description: s.description || '',
      logo_url: s.logo_url || '',
      website_url: s.website_url || '',
      location: s.location || '',
      is_active: !!s.is_active,
      sort_order: s.sort_order || 0,
    });
  };

  const startNew = () => {
    setSelected(null);
    setForm(EMPTY);
  };

  const save = async () => {
    if (!form.name.trim()) {
      toast.error('Sponsor name is required');
      return;
    }
    setSaving(true);
    try {
      if (selected) {
        await sponsorsApi.update(selected, form);
        toast.success('Sponsor updated');
      } else {
        const res = await sponsorsApi.create(form);
        setSelected(res.data.id);
        toast.success('Sponsor created');
      }
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this sponsor permanently?')) return;
    try {
      await sponsorsApi.delete(id);
      toast.success('Sponsor deleted');
      if (selected === id) startNew();
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const uploadLogo = async (file) => {
    if (!selected) {
      toast.error('Save the sponsor first, then upload a logo');
      return;
    }
    setUploading(true);
    try {
      const res = await sponsorsApi.uploadLogo(selected, file);
      setForm((f) => ({ ...f, logo_url: res.data.logo_url }));
      toast.success('Logo uploaded');
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8" data-testid="admin-sponsors-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-3xl font-bold text-white">Manage Sponsors</h1>
          <p className="text-sm text-gray-400 mt-1">Add, edit, and reorder your Crown Vic Nation sponsors.</p>
        </div>
        <Button onClick={startNew} className="bg-[#FF3B30] hover:bg-[#E6352B] gap-2" data-testid="admin-new-sponsor-btn">
          <Plus className="w-4 h-4" /> New Sponsor
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Sidebar list */}
        <div className="lg:col-span-1 space-y-2">
          {loading && <p className="text-gray-400 text-sm">Loading…</p>}
          {!loading && sponsors.length === 0 && (
            <p className="text-gray-500 text-sm">No sponsors yet.</p>
          )}
          {sponsors.map((s) => (
            <button
              key={s.id}
              onClick={() => pickForEdit(s)}
              className={`w-full text-left p-3 rounded-lg border transition-all ${
                selected === s.id
                  ? 'border-[#FF3B30] bg-[#FF3B30]/10'
                  : 'border-white/10 bg-[#141414] hover:border-white/30'
              }`}
              data-testid={`admin-sponsor-row-${s.id}`}
            >
              <div className="flex items-center gap-3">
                {s.logo_url ? (
                  <img
                    src={resolveImageUrl(s.logo_url)}
                    alt=""
                    className="w-12 h-12 object-contain rounded bg-white/5"
                  />
                ) : (
                  <div className="w-12 h-12 rounded bg-white/5" />
                )}
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-white truncate">{s.name}</p>
                  <p className="text-[10px] uppercase tracking-widest text-gray-500 mt-0.5">
                    {s.tier} {!s.is_active && '• Hidden'}
                  </p>
                </div>
              </div>
            </button>
          ))}
        </div>

        {/* Editor */}
        <div className="lg:col-span-2 bg-[#141414] border border-white/10 rounded-xl p-6 space-y-5">
          <div className="flex items-center justify-between">
            <h2 className="font-heading text-xl font-bold text-white">
              {selected ? 'Edit Sponsor' : 'New Sponsor'}
            </h2>
            {selected && (
              <Button
                variant="ghost"
                className="text-red-400 hover:text-red-300 gap-2"
                onClick={() => remove(selected)}
                data-testid="admin-delete-sponsor-btn"
              >
                <Trash2 className="w-4 h-4" /> Delete
              </Button>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-gray-300">Name *</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="bg-[#0A0A0A] border-white/10 text-white mt-1"
                data-testid="admin-sponsor-name-input"
              />
            </div>
            <div>
              <Label className="text-gray-300">Tier</Label>
              <Select value={form.tier} onValueChange={(v) => setForm({ ...form, tier: v })}>
                <SelectTrigger className="bg-[#0A0A0A] border-white/10 text-white mt-1" data-testid="admin-sponsor-tier-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#141414] border-white/10">
                  {TIERS.map((t) => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label className="text-gray-300">Website URL</Label>
            <Input
              value={form.website_url}
              onChange={(e) => setForm({ ...form, website_url: e.target.value })}
              className="bg-[#0A0A0A] border-white/10 text-white mt-1"
              placeholder="https://…"
              data-testid="admin-sponsor-url-input"
            />
          </div>

          <div>
            <Label className="text-gray-300">Location</Label>
            <Input
              value={form.location}
              onChange={(e) => setForm({ ...form, location: e.target.value })}
              className="bg-[#0A0A0A] border-white/10 text-white mt-1"
              placeholder="City, State"
              data-testid="admin-sponsor-location-input"
            />
          </div>

          <div>
            <Label className="text-gray-300">Tagline (short one-liner)</Label>
            <Input
              value={form.tagline}
              onChange={(e) => setForm({ ...form, tagline: e.target.value })}
              className="bg-[#0A0A0A] border-white/10 text-white mt-1"
              maxLength={240}
              data-testid="admin-sponsor-tagline-input"
            />
          </div>

          <div>
            <Label className="text-gray-300">Description</Label>
            <Textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={8}
              className="bg-[#0A0A0A] border-white/10 text-white mt-1"
              maxLength={4000}
              data-testid="admin-sponsor-description-input"
            />
          </div>

          <div>
            <Label className="text-gray-300">Logo</Label>
            <div className="mt-1 flex items-center gap-4">
              {form.logo_url ? (
                <img
                  src={resolveImageUrl(form.logo_url)}
                  alt="logo preview"
                  className="w-24 h-24 object-contain rounded bg-white/5"
                />
              ) : (
                <div className="w-24 h-24 rounded bg-white/5 border border-dashed border-white/20 flex items-center justify-center text-xs text-gray-500">
                  No logo
                </div>
              )}
              <label className="cursor-pointer">
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => e.target.files?.[0] && uploadLogo(e.target.files[0])}
                  data-testid="admin-sponsor-logo-upload"
                />
                <span className="inline-flex items-center gap-2 px-4 py-2 bg-[#0A0A0A] border border-white/10 rounded-md text-sm text-white hover:border-white/30">
                  <Upload className="w-4 h-4" />
                  {uploading ? 'Uploading…' : (selected ? 'Upload New Logo' : 'Save first, then upload')}
                </span>
              </label>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Or paste a direct image URL in the field below.
            </p>
            <Input
              value={form.logo_url}
              onChange={(e) => setForm({ ...form, logo_url: e.target.value })}
              className="bg-[#0A0A0A] border-white/10 text-white mt-2"
              placeholder="https://…/logo.png"
              data-testid="admin-sponsor-logo-url-input"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-gray-300">Sort Order</Label>
              <Input
                type="number"
                value={form.sort_order}
                onChange={(e) => setForm({ ...form, sort_order: parseInt(e.target.value || '0', 10) })}
                className="bg-[#0A0A0A] border-white/10 text-white mt-1"
              />
              <p className="text-xs text-gray-500 mt-1">Lower numbers show first within the same tier.</p>
            </div>
            <div className="flex items-center gap-3 pt-6">
              <Switch
                checked={form.is_active}
                onCheckedChange={(v) => setForm({ ...form, is_active: v })}
                data-testid="admin-sponsor-active-switch"
              />
              <Label className="text-gray-300">Visible on public /sponsors page</Label>
            </div>
          </div>

          <div className="flex items-center justify-between pt-4 border-t border-white/10">
            {form.website_url && (
              <a
                href={form.website_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-gray-400 hover:text-white flex items-center gap-1"
              >
                Preview link <ExternalLink className="w-3 h-3" />
              </a>
            )}
            <Button
              onClick={save}
              disabled={saving}
              className="bg-[#FF3B30] hover:bg-[#E6352B] gap-2 ml-auto"
              data-testid="admin-sponsor-save-btn"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Saving…' : (selected ? 'Save Changes' : 'Create Sponsor')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
